import { useState, useCallback, useEffect, useRef } from 'react';
import {
  fetchPracticeWords,
  fetchCharacterProgress,
  recordCharacterAttempt,
  recordSessionResults,
  ensureHanziExampleSentences,
  HanziWord,
  HanziConfig,
} from '../../services/hanziHeroService';

interface UseHanziSessionParams {
  tuteeId: string;
  studentName?: string;
  config: HanziConfig;
}

export type SessionPhase =
  | 'loading'
  | 'practicing'
  | 'self_check'
  | 'session_complete'
  | 'wheel'
  /** Check-in saved; streak below configured threshold so wheel is skipped */
  | 'recorded_no_wheel';

export interface UseHanziSessionReturn {
  words: HanziWord[];
  sessionWords: HanziWord[];
  currentIndex: number;
  currentWord: HanziWord | null;
  phase: SessionPhase;
  streak: number;
  correctCount: number;
  progress: { attemptCount: number; correctCount: number; highestStreak: number } | null;
  /** Per-character progress for current session words */
  characterProgress: Record<string, { attemptCount: number; correctCount: number }>;
  /** Last self-check outcome per character this session (overwritten on “practice again”) */
  sessionOutcomeByCharacter: Record<string, boolean>;
  /** Call when quiz/attempt completes to record result */
  handleQuizComplete: (character: string, correct: boolean) => void;
  markOk: () => void;
  markPracticeAgain: () => void;
  advanceToNext: () => void;
  completeSession: () => void;
  isLoading: boolean;
  error: string | null;
  /** True while Edge Function is generating example sentences for the current item */
  isEnsuringExamples: boolean;
}

export const useHanziSession = ({
  tuteeId,
  studentName,
  config,
}: UseHanziSessionParams): UseHanziSessionReturn => {
  const [words, setWords] = useState<HanziWord[]>([]);
  const [sessionWords, setSessionWords] = useState<HanziWord[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [phase, setPhase] = useState<SessionPhase>('loading');
  const [streak, setStreak] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [progress, setProgress] = useState<{
    attemptCount: number;
    correctCount: number;
    highestStreak: number;
  } | null>(null);
  const [characterProgress, setCharacterProgress] = useState<
    Record<string, { attemptCount: number; correctCount: number }>
  >({});
  const [sessionOutcomeByCharacter, setSessionOutcomeByCharacter] = useState<Record<string, boolean>>(
    {}
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEnsuringExamples, setIsEnsuringExamples] = useState(false);

  /** Avoid duplicate OpenAI calls for the same word (StrictMode double effect, quick re-renders). */
  const ensureInFlightRef = useRef<Set<string>>(new Set());
  const ensureSucceededRef = useRef<Set<string>>(new Set());

  const perSession = config.charactersPerSession ?? 5;

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        setError(null);
        ensureSucceededRef.current.clear();
        ensureInFlightRef.current.clear();
        const list = await fetchPracticeWords(tuteeId, studentName, config);
        setWords(list);

        const shuffled = [...list].sort(() => Math.random() - 0.5);
        const selected = shuffled.slice(0, Math.min(perSession, list.length));
        setSessionWords(selected);
        setCurrentIndex(0);
        setCorrectCount(0);
        setSessionOutcomeByCharacter({});

        if (selected.length > 0) {
          const chars = selected.map((w) => w.character);
          const prog = await fetchCharacterProgress(tuteeId, studentName ?? '', chars);
          setCharacterProgress(prog);
        } else {
          setCharacterProgress({});
        }
        setPhase(selected.length > 0 ? 'practicing' : 'session_complete');
      } catch (err) {
        console.error(err);
        setError('Failed to load characters');
        setPhase('session_complete');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [
    tuteeId,
    studentName,
    perSession,
    config.characterSource,
    config.moeTypes?.join(','),
    config.moeType,
    config.moeLevels?.join(','),
    config.moeLevel,
    config.moeLessons?.join(','),
  ]);

  const currentWord = sessionWords[currentIndex] ?? null;

  useEffect(() => {
    if (!currentWord || phase !== 'practicing') {
      setIsEnsuringExamples(false);
      return;
    }
    const hasExamples =
      (currentWord.exampleSentences && currentWord.exampleSentences.length > 0) ||
      !!(currentWord.exampleSentence && currentWord.exampleSentence.trim());
    if (hasExamples) {
      setIsEnsuringExamples(false);
      return;
    }

    const wordId = currentWord.id;
    if (ensureSucceededRef.current.has(wordId) || ensureInFlightRef.current.has(wordId)) {
      setIsEnsuringExamples(ensureInFlightRef.current.has(wordId));
      return;
    }

    ensureInFlightRef.current.add(wordId);
    setIsEnsuringExamples(true);
    (async () => {
      try {
        const sentences = await ensureHanziExampleSentences(wordId);
        ensureSucceededRef.current.add(wordId);
        const apply = (w: HanziWord) =>
          w.id === wordId ? { ...w, exampleSentences: sentences } : w;
        setSessionWords((prev) => prev.map(apply));
        setWords((prev) => prev.map(apply));
      } catch (e) {
        console.error('ensureHanziExampleSentences', e);
      } finally {
        ensureInFlightRef.current.delete(wordId);
        setIsEnsuringExamples(false);
      }
    })();
  }, [currentWord?.id, phase, currentIndex]);

  const advanceToNext = useCallback(() => {
    setPhase('practicing');
    setCurrentIndex((i) => i + 1);
  }, []);

  /** Record an attempt for the current character. Call when quiz/self-check completes. */
  const handleQuizComplete = useCallback(
    (character: string, correct: boolean) => {
      setSessionOutcomeByCharacter((prev) => ({ ...prev, [character]: correct }));
      recordCharacterAttempt(tuteeId, studentName ?? '', character, correct);
      if (correct) setCorrectCount((c) => c + 1);
      setCharacterProgress((prev) => {
        const stats = prev[character] ?? { attemptCount: 0, correctCount: 0 };
        return {
          ...prev,
          [character]: {
            attemptCount: stats.attemptCount + 1,
            correctCount: stats.correctCount + (correct ? 1 : 0),
          },
        };
      });
    },
    [tuteeId, studentName]
  );

  /** Advance to the next word (or end session). Session `correctCount` is updated only via `handleQuizComplete`. */
  const markOk = useCallback(() => {
    if (currentIndex < sessionWords.length - 1) {
      setPhase('practicing');
      setCurrentIndex((i) => i + 1);
    } else {
      setPhase('session_complete');
    }
  }, [currentIndex, sessionWords.length]);

  const markPracticeAgain = useCallback(() => {
    setPhase('practicing');
  }, []);

  const completeSession = useCallback(async () => {
    try {
      const studentKey = studentName ?? '';
      const { streak: newStreak, progress: newProgress } = await recordSessionResults(
        tuteeId,
        studentKey,
        sessionWords.length,
        correctCount
      );
      setStreak(newStreak);
      setProgress(
        newProgress
          ? {
              attemptCount: newProgress.attemptCount,
              correctCount: newProgress.correctCount,
              highestStreak: newProgress.highestStreak,
            }
          : null
      );
      const minStreak = Math.max(1, config.prizeWheelMinStreak ?? 1);
      setPhase(newStreak >= minStreak ? 'wheel' : 'recorded_no_wheel');
    } catch (err) {
      console.error(err);
      setPhase('session_complete');
    }
  }, [tuteeId, studentName, sessionWords.length, correctCount, config.prizeWheelMinStreak]);

  return {
    words,
    sessionWords,
    currentIndex,
    currentWord,
    phase,
    streak,
    correctCount,
    progress,
    characterProgress,
    sessionOutcomeByCharacter,
    handleQuizComplete,
    markOk,
    markPracticeAgain,
    advanceToNext,
    completeSession,
    isLoading,
    error,
    isEnsuringExamples,
  };
};
